import React from 'react';
import SEO from '../components/SEO';
import TransitionLink from '../components/TransitionLink';
import LightHeader from '../components/LightHeader';
import LightFooter from '../components/LightFooter';
import { useLanguage } from '../context/LanguageContext';

const content = {
    en: {
        tag: 'Broadcast / Virtual Production',
        h1a: 'Broadcast Designer',
        h1b: 'and Virtual Production',
        h1c: 'Specialist',
        bio: "I've worked in real-time broadcast graphics for Italian national television for fifteen years, including RAI, LA7 and Dolce & Gabbana. Virtual studios, augmented reality, live direction.",
        getInTouch: 'Get in touch',
        credentials: [
            { value: '15+', label: 'Years in broadcast' },
            { value: 'RAI / LA7', label: 'National television' },
            { value: 'Unreal Engine', label: 'Real-time pipeline' },
            { value: 'Live', label: 'Live broadcast delivery' },
        ],
        selectedWork: 'Selected Work',
        viewProject: 'View project',
        toolsTitle: 'Tools and Technologies',
        ctaText: 'Fifteen years of live production. Happy to talk about broadcast or virtual production work.',
        fullPortfolio: 'Full portfolio',
        projects: [
            {
                id: '1',
                title: 'Viva Rai2!',
                category: 'Motion Graphics / Live Broadcast',
                year: '2023-2024',
                description: "Daily real-time graphics for Fiorello's national morning show on Rai 2. 5am starts, live direction changes, eight hours of broadcast every day.",
                image: '/img/work1.png',
                link: '/work/1',
            },
            {
                id: '3',
                title: 'Ulisse — Alberto Angela',
                category: 'Virtual Production',
                year: '2020-2022',
                description: "Full virtual production pipeline for one of Italian television's most watched documentary formats. Real-time rendering, camera tracking, live compositing.",
                image: '/img/work3.png',
                link: '/work/3',
            },
            {
                id: '2',
                title: 'Dolce & Gabbana',
                category: 'Virtual Production',
                year: '2021',
                description: 'Virtual production for a Dolce & Gabbana fashion event. LED wall systems, real-time rendering, full art direction on set.',
                image: '/img/work2.png',
                link: '/work/2',
            },
        ],
    },
    it: {
        tag: 'Broadcast / Virtual Production',
        h1a: 'Broadcast Designer',
        h1b: 'e Specialista in',
        h1c: 'Virtual Production',
        bio: 'Ho lavorato per quindici anni nella grafica broadcast in tempo reale per la televisione nazionale italiana, tra cui RAI, LA7 e Dolce & Gabbana. Studi virtuali, realta aumentata, direzione live.',
        getInTouch: 'Contattami',
        credentials: [
            { value: '15+', label: 'Anni nel broadcast' },
            { value: 'RAI / LA7', label: 'Televisione nazionale' },
            { value: 'Unreal Engine', label: 'Pipeline real-time' },
            { value: 'Live', label: 'Consegna in diretta' },
        ],
        selectedWork: 'Lavori Selezionati',
        viewProject: 'Vedi progetto',
        toolsTitle: 'Strumenti e Tecnologie',
        ctaText: 'Quindici anni di produzione live. Disponibile a parlare di progetti broadcast o virtual production.',
        fullPortfolio: 'Portfolio completo',
        projects: [
            {
                id: '1',
                title: 'Viva Rai2!',
                category: 'Motion Graphics / Broadcast Live',
                year: '2023-2024',
                description: "Grafica in tempo reale ogni giorno per il morning show nazionale di Fiorello su Rai 2. Sveglie alle 5, variazioni live, otto ore di broadcast al giorno.",
                image: '/img/work1.png',
                link: '/work/1',
            },
            {
                id: '3',
                title: 'Ulisse — Alberto Angela',
                category: 'Virtual Production',
                year: '2020-2022',
                description: "Pipeline di virtual production completa per uno dei formati documentaristici piu visti della televisione italiana. Rendering real-time, camera tracking, compositing live.",
                image: '/img/work3.png',
                link: '/work/3',
            },
            {
                id: '2',
                title: 'Dolce & Gabbana',
                category: 'Virtual Production',
                year: '2021',
                description: 'Virtual production per un evento fashion Dolce & Gabbana. Sistemi LED wall, rendering real-time, direzione artistica completa sul set.',
                image: '/img/work2.png',
                link: '/work/2',
            },
        ],
    },
};

const tools = [
    'Unreal Engine', 'Cinema 4D', 'Blender', 'After Effects',
    'Pixotope', 'Avid Maestro', 'ClassX', 'Real-time Rendering',
    'Virtual Studio', 'Augmented Reality', 'Camera Tracking', 'LED Wall Systems',
];

const LandingBroadcast = () => {
    const { language } = useLanguage();
    const t = content[language];

    return (
        <>
            <SEO
                title={language === 'it' ? 'Broadcast Designer e Specialista Virtual Production' : 'Broadcast Designer and Virtual Production Specialist'}
                description={language === 'it'
                    ? 'Quindici anni a costruire grafica broadcast in tempo reale per la televisione nazionale italiana. Studi virtuali, realta aumentata, direzione live.'
                    : 'Fifteen years building real-time broadcast graphics for Italian national television. Virtual studios, augmented reality, live direction.'}
                url="/landing-broadcast"
                language={language}
            />

            <div className="min-h-screen bg-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>

                <LightHeader ctaLabel={t.getInTouch} />

                {/* Hero */}
                <section className="pt-40 pb-24 px-8 md:px-16 max-w-6xl mx-auto">
                    <p className="text-orange-400 text-sm font-mono tracking-widest uppercase mb-6">
                        {t.tag}
                    </p>
                    <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-white tracking-tight leading-none mb-10">
                        {t.h1a}<br />
                        <span className="text-orange-400">{t.h1b}</span><br />
                        {t.h1c}
                    </h1>
                    <p className="text-xl md:text-2xl text-white/70 font-light leading-relaxed max-w-2xl mb-12">
                        {t.bio}
                    </p>
                    <a
                        href="mailto:info@paoloallegretti.com"
                        className="inline-flex items-center text-white font-medium text-lg group"
                    >
                        <span className="border-b border-white/30 group-hover:border-orange-400 group-hover:text-orange-400 transition-colors duration-300 pb-0.5">
                            info@paoloallegretti.com
                        </span>
                        <span className="ml-2 group-hover:translate-x-1 transition-transform duration-300">→</span>
                    </a>
                </section>

                {/* Credentials bar */}
                <section className="border-t border-b border-white/10 py-8 px-8 md:px-16">
                    <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
                        {t.credentials.map((item) => (
                            <div key={item.label}>
                                <p className="text-2xl md:text-3xl font-black text-white mb-1">{item.value}</p>
                                <p className="text-sm text-white/40 font-mono">{item.label}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Projects */}
                <section className="py-24 px-8 md:px-16 max-w-6xl mx-auto">
                    <h2 className="text-sm font-mono text-white/40 tracking-widest uppercase mb-16">{t.selectedWork}</h2>
                    <div className="space-y-6">
                        {t.projects.map((project) => (
                            <TransitionLink
                                key={project.id}
                                to={project.link}
                                className="group block border border-white/10 rounded-2xl overflow-hidden hover:border-orange-400/40 transition-colors duration-300"
                            >
                                <div className="flex flex-col md:flex-row">
                                    <div className="w-full md:w-64 h-48 md:h-auto bg-white/5 flex-shrink-0 overflow-hidden">
                                        <img
                                            src={project.image}
                                            alt={project.title}
                                            className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                                        />
                                    </div>
                                    <div className="p-8 flex flex-col justify-between flex-1">
                                        <div>
                                            <div className="flex items-center gap-4 mb-4">
                                                <span className="text-xs font-mono text-orange-400 tracking-widest uppercase">{project.category}</span>
                                                <span className="text-xs font-mono text-white/30">{project.year}</span>
                                            </div>
                                            <h3 className="text-2xl md:text-3xl font-black text-white mb-3 tracking-tight group-hover:text-orange-400 transition-colors duration-300">
                                                {project.title}
                                            </h3>
                                            <p className="text-white/50 leading-relaxed">{project.description}</p>
                                        </div>
                                        <div className="mt-6">
                                            <span className="text-sm text-white/30 font-mono group-hover:text-orange-400 transition-colors duration-300">
                                                {t.viewProject} →
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </TransitionLink>
                        ))}
                    </div>
                </section>

                {/* Tools */}
                <section className="py-16 px-8 md:px-16 border-t border-white/10">
                    <div className="max-w-6xl mx-auto">
                        <h2 className="text-sm font-mono text-white/40 tracking-widest uppercase mb-10">{t.toolsTitle}</h2>
                        <div className="flex flex-wrap gap-3">
                            {tools.map((skill) => (
                                <span
                                    key={skill}
                                    className="px-4 py-2 border border-white/20 rounded-full text-sm text-white/60 font-mono hover:border-orange-400/50 hover:text-orange-400 transition-colors duration-300"
                                >
                                    {skill}
                                </span>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Contact */}
                <section className="py-24 px-8 md:px-16">
                    <div className="max-w-6xl mx-auto">
                        <p className="text-3xl md:text-5xl font-black text-white tracking-tight leading-tight mb-10 max-w-2xl">
                            {t.ctaText}
                        </p>
                        <a
                            href="mailto:info@paoloallegretti.com"
                            className="text-3xl md:text-5xl font-black text-white/30 hover:text-orange-400 transition-colors duration-300 tracking-tight"
                        >
                            info@paoloallegretti.com
                        </a>
                        <LightFooter fullPortfolioLabel={t.fullPortfolio} />
                    </div>
                </section>

            </div>
        </>
    );
};

export default LandingBroadcast;
